import React, { useMemo } from "react";
import { Line, Bar } from "react-chartjs-2";
import { ChartOptions } from "chart.js";
import {
  useVizPalette,
  opcoesBase,
  legendStyle,
  WEEKDAYS,
  shortDay,
} from "./vizShared.ts";
import { tipoInfo } from "../../components/tickets/ticketTypes.ts";

// ─── Formato dos dados (espelha o /api/admin/analytics/helpdesk) ────────────

export interface HelpdeskSeries {
  byStatus: Record<string, number>;
  /** Por tipo, só o que o atendente classificou. */
  byType: { type: string; count: number }[];
  /** Tipos escolhidos pelo solicitante, antes da mudança (histórico). */
  byTypeSolicitante?: { type: string; count: number }[];
  bySystem: { name: string; count: number }[];
  daily: { dia: string; abertos: number; concluidos: number }[];
  byWeekdayPeriod: { dow: number; periodo: number; count: number }[];
  monthly: {
    mes: string;
    horas: number | null;
    horasP90: number | null;
    concluidos: number;
    espera: number | null;
    esperaP90: number | null;
    iniciados: number;
  }[];
  byAttendant: { name: string; count: number }[];
  /** Avaliação do atendimento (opcional: backend antigo não manda). */
  rating?: {
    total: number;
    media: number | null;
    concluidos: number;
    /** Quantidade de notas 1, 2, 3, 4 e 5. */
    distribuicao: number[];
  };
  ratingByAttendant?: { name: string; media: number; count: number }[];
}

/** 4.6 → "4,6" */
export const formatNota = (n: number | null | undefined) =>
  n == null ? "—" : n.toFixed(1).replace(".", ",");

// Ordem do funil de atendimento. "pausado" fica fora da escala (é um estado,
// não uma etapa), por isso recebe o cinza neutro.
const STATUS_FUNNEL = [
  { key: "novo", label: "Recebido" },
  { key: "analise", label: "Em análise" },
  { key: "andamento", label: "Em atendimento" },
  { key: "concluido", label: "Concluído" },
];

// Períodos do dia, na ordem de um dia que começa às 8h. Os índices são os
// mesmos que o backend devolve em `periodo`.
const PERIODOS = ["08–10", "10–13", "13–15", "15–18", "18–00", "00–08"];
const GRUPOS_PERIODO = [
  { rotulo: "Manhã", colunas: 2 },
  { rotulo: "Tarde", colunas: 2 },
  { rotulo: "Fora do expediente", colunas: 2 },
];

const MONTHS = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

const shortMonth = (iso: string) => {
  const [y, m] = iso.split("-");
  return `${MONTHS[Number(m) - 1]}/${y.slice(2)}`;
};

// Exportado para o card de KPI e o gráfico escreverem o mesmo valor do
// mesmo jeito. Vírgula decimal, como no resto do painel.
export const formatHoursShort = (h: number | null): string => {
  // `== null` cobre também undefined (backend anterior sem o campo).
  if (h == null || Number.isNaN(h)) return "—";
  if (h < 1) return `${Math.round(h * 60)}min`;
  if (h < 24) return `${h.toFixed(1).replace(".", ",")}h`;
  return `${Math.round(h / 24)}d`;
};

interface Props {
  stats: HelpdeskSeries;
}

const HelpdeskCharts: React.FC<Props> = ({ stats }) => {
  const palette = useVizPalette();

  const baseOptions = useMemo<ChartOptions<any>>(
    () => opcoesBase(palette),
    [palette],
  );

  // ── 1. Abertos x concluídos (30 dias) ──
  const dailyData = useMemo(() => {
    const labels = stats.daily.map((d) => shortDay(d.dia));
    return {
      labels,
      datasets: [
        {
          label: "Abertos",
          data: stats.daily.map((d) => d.abertos),
          borderColor: palette.cat1,
          backgroundColor: `${palette.cat1}22`,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.35,
          fill: true,
        },
        {
          label: "Concluídos",
          data: stats.daily.map((d) => d.concluidos),
          borderColor: palette.cat2,
          backgroundColor: `${palette.cat2}22`,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [stats.daily, palette]);

  const dailyOptions = useMemo<ChartOptions<any>>(
    () => ({
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: legendStyle(palette),
      },
      scales: {
        ...baseOptions.scales,
        x: {
          ...(baseOptions.scales as any).x,
          ticks: {
            ...(baseOptions.scales as any).x.ticks,
            maxTicksLimit: 10,
            autoSkip: true,
          },
        },
      },
    }),
    [baseOptions, palette],
  );

  // ── 2. Barras horizontais (tipo e canal) ──
  const horizontalOptions = useMemo<ChartOptions<any>>(
    () => ({
      ...baseOptions,
      indexAxis: "y" as const,
      interaction: { mode: "nearest" as const, intersect: true },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: palette.grid },
          border: { display: false },
          ticks: {
            color: palette.textMuted,
            font: { size: 11 },
            precision: 0,
          },
        },
        y: {
          grid: { display: false },
          border: { color: palette.grid },
          ticks: { color: palette.text, font: { size: 12 } },
        },
      },
    }),
    [baseOptions, palette],
  );

  const typeData = useMemo(() => {
    const rows = [...stats.byType].sort((a, b) => b.count - a.count);
    return {
      labels: rows.map((r) => tipoInfo(r.type).label),
      datasets: [
        {
          data: rows.map((r) => r.count),
          backgroundColor: palette.seq[2],
          hoverBackgroundColor: palette.seq[3],
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 22,
        },
      ],
    };
  }, [stats.byType, palette]);

  const systemData = useMemo(() => {
    const rows = [...stats.bySystem].sort((a, b) => b.count - a.count);
    return {
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => r.count),
          backgroundColor: palette.seq[2],
          hoverBackgroundColor: palette.seq[3],
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    };
  }, [stats.bySystem, palette]);

  const attendantData = useMemo(() => {
    const rows = [...stats.byAttendant].sort((a, b) => b.count - a.count);
    return {
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => r.count),
          backgroundColor: palette.seq[2],
          hoverBackgroundColor: palette.seq[3],
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    };
  }, [stats.byAttendant, palette]);

  // ── Avaliações: distribuição das notas (1 a 5) ──
  // Rampa sequencial da marca: nota maior, tom mais forte.
  const ratingDist = useMemo(
    () => stats.rating?.distribuicao ?? [0, 0, 0, 0, 0],
    [stats.rating],
  );
  const ratingTotal = stats.rating?.total ?? 0;
  const ratingDistData = useMemo(
    () => ({
      labels: ["1 ★", "2 ★", "3 ★", "4 ★", "5 ★"],
      datasets: [
        {
          data: ratingDist,
          backgroundColor: palette.seq,
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    }),
    [ratingDist, palette],
  );

  const ratingDistOptions = useMemo<ChartOptions<any>>(
    () => ({
      ...baseOptions,
      interaction: { mode: "nearest" as const, intersect: true },
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          ...(baseOptions.plugins as any).tooltip,
          displayColors: false,
          callbacks: {
            title: (items: any[]) => `${items[0].dataIndex + 1} de 5 estrelas`,
            label: (ctx: any) => {
              const pct = ratingTotal ? Math.round((ctx.raw / ratingTotal) * 100) : 0;
              return ` ${ctx.raw} avaliação(ões) · ${pct}%`;
            },
          },
        },
      },
    }),
    [baseOptions, ratingTotal],
  );

  // ── Avaliações: média por atendente (escala fixa de 0 a 5) ──
  const ratingAtt = useMemo(
    () => stats.ratingByAttendant ?? [],
    [stats.ratingByAttendant],
  );
  const ratingAttData = useMemo(
    () => ({
      labels: ratingAtt.map((r) => r.name),
      datasets: [
        {
          data: ratingAtt.map((r) => Number(r.media.toFixed(2))),
          backgroundColor: palette.seq[2],
          hoverBackgroundColor: palette.seq[3],
          borderRadius: 4,
          borderSkipped: false,
          barThickness: 18,
        },
      ],
    }),
    [ratingAtt, palette],
  );

  const ratingAttOptions = useMemo<ChartOptions<any>>(
    () => ({
      ...horizontalOptions,
      scales: {
        ...(horizontalOptions as any).scales,
        x: {
          ...(horizontalOptions as any).scales.x,
          min: 0,
          max: 5,
          ticks: { ...(horizontalOptions as any).scales.x.ticks, stepSize: 1 },
        },
      },
      plugins: {
        ...horizontalOptions.plugins,
        tooltip: {
          ...(horizontalOptions.plugins as any).tooltip,
          displayColors: false,
          callbacks: {
            label: (ctx: any) => {
              const r = ratingAtt[ctx.dataIndex];
              return ` ${formatNota(r?.media)} de 5 · ${r?.count ?? 0} avaliação(ões)`;
            },
          },
        },
      },
    }),
    [horizontalOptions, ratingAtt],
  );

  // ── 3. Tempo médio de resolução por mês ──
  const monthlyData = useMemo(
    () => ({
      labels: stats.monthly.map((m) => shortMonth(m.mes)),
      datasets: [
        {
          label: "Tempo de resolução",
          data: stats.monthly.map((m) =>
            m.horas == null ? null : Number(m.horas.toFixed(2)),
          ),
          borderColor: palette.cat2,
          backgroundColor: `${palette.cat2}22`,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: palette.cat2,
          tension: 0.3,
          fill: true,
        },
        {
          // Tracejada: a distinção entre as duas linhas não fica só na cor.
          // Mesma estatística e mesma régua da resolução (mediana, horas úteis).
          label: "Tempo até o início",
          data: stats.monthly.map((m) =>
            m.espera == null ? null : Number(m.espera.toFixed(2)),
          ),
          borderColor: palette.seq[1],
          backgroundColor: `${palette.seq[1]}22`,
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: palette.seq[1],
          tension: 0.3,
          fill: false,
        },
      ],
    }),
    [stats.monthly, palette],
  );

  const monthlyOptions = useMemo<ChartOptions<any>>(
    () => ({
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        legend: legendStyle(palette),
        tooltip: {
          ...(baseOptions.plugins as any).tooltip,
          callbacks: {
            // As duas linhas são a mediana; o p90 vai no tooltip para a
            // cauda (o chamado esquecido) não sumir do gráfico.
            label: (ctx: any) => {
              const m = stats.monthly[ctx.dataIndex];
              if (ctx.datasetIndex === 1) {
                return ` Até o início: ${formatHoursShort(m?.espera ?? null)} · 90% em até ${formatHoursShort(m?.esperaP90 ?? null)} · ${m?.iniciados ?? 0} atendido(s)`;
              }
              return ` Resolução: ${formatHoursShort(m?.horas ?? null)} · 90% em até ${formatHoursShort(m?.horasP90 ?? null)} · ${m?.concluidos ?? 0} concluído(s)`;
            },
          },
        },
      },
    }),
    [baseOptions, stats.monthly, palette],
  );

  // ── 4. Funil de status (barra empilhada em HTML) ──
  const funnel = useMemo(() => {
    const itens = STATUS_FUNNEL.map((s, i) => ({
      ...s,
      count: stats.byStatus[s.key] || 0,
      color: palette.seq[i],
    }));
    const pausado = stats.byStatus.pausado || 0;
    if (pausado > 0) {
      itens.push({
        key: "pausado",
        label: "Pausado",
        count: pausado,
        color: palette.neutral,
      });
    }
    const total = itens.reduce((acc, i) => acc + i.count, 0);
    return { itens, total };
  }, [stats.byStatus, palette]);

  // ── 5. Mapa de calor (dia da semana x período do dia) ──
  const heatmap = useMemo(() => {
    const grid: number[][] = WEEKDAYS.map(() => PERIODOS.map(() => 0));
    // `?? []`: um backend ainda sem o campo não derruba a tela.
    (stats.byWeekdayPeriod ?? []).forEach((c) => {
      if (grid[c.dow] && grid[c.dow][c.periodo] !== undefined) {
        grid[c.dow][c.periodo] = c.count;
      }
    });
    const max = Math.max(0, ...grid.flat());
    return { grid, max };
  }, [stats.byWeekdayPeriod]);

  const heatColor = (n: number) => {
    if (!heatmap.max || n === 0) return "var(--viz-empty)";
    // 5 degraus da rampa sequencial, proporcionais ao máximo.
    const step = Math.min(4, Math.floor((n / heatmap.max) * 5 - 0.0001) + 0);
    return palette.seq[Math.max(0, step)];
  };

  const semDados = funnel.total === 0;
  const tiposDoSolicitante = (stats.byTypeSolicitante ?? []).reduce(
    (acc, r) => acc + r.count,
    0,
  );

  return (
    <div className="viz-section">
      {/* ── Evolução ── */}
      <div className="viz-card viz-card--wide">
        <div className="viz-card-head">
          <h3>Abertos x Concluídos</h3>
          <span className="viz-card-sub">últimos 30 dias</span>
        </div>
        <div className="viz-canvas viz-canvas--tall">
          <Line data={dailyData} options={dailyOptions} />
        </div>
      </div>

      {/* ── Funil de status ── */}
      <div className="viz-card viz-card--wide">
        <div className="viz-card-head">
          <h3>Distribuição por status</h3>
          <span className="viz-card-sub">
            {funnel.total} chamado{funnel.total === 1 ? "" : "s"}
          </span>
        </div>

        {semDados ? (
          <p className="viz-empty">Sem chamados registrados ainda.</p>
        ) : (
          <>
            <div
              className="viz-stack"
              role="img"
              aria-label={funnel.itens
                .map((i) => `${i.label}: ${i.count}`)
                .join(", ")}
            >
              {funnel.itens
                .filter((i) => i.count > 0)
                .map((i) => (
                  <span
                    key={i.key}
                    className="viz-stack-seg"
                    title={`${i.label}: ${i.count}`}
                    style={
                      {
                        "--seg-w": `${(i.count / funnel.total) * 100}%`,
                        "--seg-c": i.color,
                      } as React.CSSProperties
                    }
                  />
                ))}
            </div>
            <ul className="viz-legend">
              {funnel.itens.map((i) => (
                <li key={i.key}>
                  <span
                    className="viz-legend-dot"
                    style={{ "--seg-c": i.color } as React.CSSProperties}
                  />
                  <span className="viz-legend-label">{i.label}</span>
                  <strong className="viz-legend-value">{i.count}</strong>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* ── Tipo e canal ── */}
      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Por tipo</h3>
          <span className="viz-card-sub">classificados pelo atendente</span>
        </div>
        <div className="viz-canvas">
          {stats.byType.length ? (
            <Bar data={typeData} options={horizontalOptions} />
          ) : (
            <p className="viz-empty">
              Nenhum chamado classificado pelo atendente ainda.
            </p>
          )}
        </div>
        {tiposDoSolicitante > 0 && (
          <p className="viz-nota">
            {tiposDoSolicitante} chamado{tiposDoSolicitante === 1 ? "" : "s"}{" "}
            de antes da mudança, com o tipo escolhido pelo próprio
            solicitante, fica{tiposDoSolicitante === 1 ? "" : "m"} fora deste
            gráfico.
          </p>
        )}
      </div>

      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Por canal de origem</h3>
        </div>
        <div className="viz-canvas">
          {stats.bySystem.length ? (
            <Bar data={systemData} options={horizontalOptions} />
          ) : (
            <p className="viz-empty">Sem dados.</p>
          )}
        </div>
      </div>

      {/* ── Mapa de calor ── */}
      <div className="viz-card viz-card--wide">
        <div className="viz-card-head">
          <h3>Quando os chamados são abertos</h3>
          <span className="viz-card-sub">dia da semana × período do dia</span>
        </div>

        {heatmap.max === 0 ? (
          <p className="viz-empty">Sem chamados registrados ainda.</p>
        ) : (
          <>
            <div className="viz-heat">
              <span />
              {GRUPOS_PERIODO.map((g) => (
                <span
                  key={g.rotulo}
                  className="viz-heat-group"
                  style={{ "--span": g.colunas } as React.CSSProperties}
                >
                  {g.rotulo}
                </span>
              ))}
              <span />
              {PERIODOS.map((h) => (
                <span key={h} className="viz-heat-col">
                  {h}
                </span>
              ))}
              {WEEKDAYS.map((dia, d) => (
                <React.Fragment key={dia}>
                  <span className="viz-heat-row">{dia}</span>
                  {PERIODOS.map((periodo, b) => (
                    <span
                      key={periodo}
                      className="viz-heat-cell"
                      title={`${dia}, ${periodo}h — ${heatmap.grid[d][b]} chamado(s)`}
                      style={
                        {
                          "--cell-c": heatColor(heatmap.grid[d][b]),
                        } as React.CSSProperties
                      }
                    >
                      {heatmap.grid[d][b] > 0 ? heatmap.grid[d][b] : ""}
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="viz-heat-scale">
              <span>menos</span>
              {palette.seq.map((c, i) => (
                <span
                  key={i}
                  className="viz-heat-scale-step"
                  style={{ "--cell-c": c } as React.CSSProperties}
                />
              ))}
              <span>mais</span>
            </div>
          </>
        )}
      </div>

      {/* ── Tempo de resolução ── */}
      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Tempos de atendimento</h3>
          <span className="viz-card-sub">mediana por mês · horário comercial</span>
        </div>
        <div className="viz-canvas">
          {stats.monthly.length ? (
            <Line data={monthlyData} options={monthlyOptions} />
          ) : (
            <p className="viz-empty">Sem chamados concluídos ainda.</p>
          )}
        </div>
      </div>

      {/* ── Atendentes ── */}
      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Concluídos por atendente</h3>
        </div>
        <div className="viz-canvas">
          {stats.byAttendant.length ? (
            <Bar data={attendantData} options={horizontalOptions} />
          ) : (
            <p className="viz-empty">Sem chamados concluídos ainda.</p>
          )}
        </div>
      </div>

      {/* ── Avaliações do atendimento ── */}
      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Avaliações do atendimento</h3>
          <span className="viz-card-sub">
            {ratingTotal
              ? `${ratingTotal} avaliação(ões) · média ${formatNota(stats.rating?.media)}`
              : "notas de 1 a 5 estrelas"}
          </span>
        </div>
        <div className="viz-canvas">
          {ratingTotal ? (
            <Bar data={ratingDistData} options={ratingDistOptions} />
          ) : (
            <p className="viz-empty">Nenhum chamado avaliado ainda.</p>
          )}
        </div>
      </div>

      <div className="viz-card">
        <div className="viz-card-head">
          <h3>Avaliação por atendente</h3>
          <span className="viz-card-sub">média de 0 a 5</span>
        </div>
        <div className="viz-canvas">
          {ratingAtt.length ? (
            <Bar data={ratingAttData} options={ratingAttOptions} />
          ) : (
            <p className="viz-empty">Nenhum chamado avaliado ainda.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpdeskCharts;
